import clsx from 'clsx';
import {
  forwardRef,
  type JSX,
  type ReactEventHandler,
  type ReactNode,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import {
  buttonStyle,
  menuItemStyles,
  styledMenuItemContent,
  styledShortcutGroup,
  styledText,
} from './Control.sty.js';
import { Button } from '../../../../../buttons/button/Button.js';
import { KeyboardShortcut } from '../../../../../content/keyboard-shortcut/KeyboardShortcut.js';
import { KeyboardShortcutTooltip } from '../../../../../content/keyboard-shortcut-tooltip/KeyboardShortcutTooltip.js';
import { useMergeRefs } from '../../../../../core/hooks/useMergeRefs.js';
import type { StylingProps } from '../../../../../core/types/styling-props.js';
import { Divider } from '../../../../../layouts/divider/Divider.js';
import { Menu } from '../../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import {
  styledHorizontalDivider,
  styledVerticalDivider,
} from '../ChartToolbarRenderer.sty.js';
import { useToolbarContext } from '../hooks/useToolbarContext.js';

/**
 * @internal
 */
export type ChartToolbarControlShortcutProps =
  | { menuShortcut: ReactNode; tooltipKeyShortcut?: never }
  | { menuShortcut?: never; tooltipKeyShortcut?: string };

/**
 * @internal
 */
export type ChartToolbarControlProps = StylingProps & {
  /** Icon to be added as a prefix */
  prefixIcon: JSX.Element;
  /** Text to be displayed when hovering the button */
  tooltipText?: string;
  /** Test id for testing purposes */
  testId?: string;
  /** Whether the button is selected or not */
  selected?: boolean;
  /** Behaviour tracking attribute */
  'data-dt-name'?: string;
  /** Behaviour tracking attribute */
  'data-dt-properties'?: string;
  /** The function called when the button is clicked */
  onClick: ReactEventHandler;
  /** The function called when a key is pressed and released when selecting this control*/
  onKeyDown?: (event: ReactKeyboardEvent) => void;
  /** The current tab index for the control */
  tabIndex?: number;
  /** Indicates whether the button is disabled, also disables the key shortcut text */
  disabled?: boolean;
  /** Forces to close the tooltip */
  hideTooltip?: boolean;
  /**
   * Indicates where to place the control.
   * 'toolbar' - control is visible in the toolbar only.
   * 'menu' - control is visible in the dropdown menu only.
   * 'auto' - when toolbar is collapsed, the control is visible in the menu, otherwise in the toolbar.
   *
   * @defaultValue 'auto'
   * */
  placement?: 'toolbar' | 'menu' | 'auto';
  /**
   * The control should be shown in the toolbar when it's collapsed.
   *
   * @defaultValue false
   * */
  showWhenCollapsed?: boolean;
  /** Indicates where to place the divider. */
  divider?: 'left' | 'right';
} & ChartToolbarControlShortcutProps;

/**
 * ChartToolbar generic control component
 * @internal
 */
export const Control = /* @__PURE__ */ forwardRef<
  HTMLButtonElement | null,
  ChartToolbarControlProps
>((props: ChartToolbarControlProps, forwardedRef) => {
  const {
    prefixIcon,
    tooltipText,
    testId,
    selected = false,
    onClick,
    onKeyDown,
    tabIndex,
    disabled = false,
    hideTooltip = false,
    tooltipKeyShortcut,
    menuShortcut,
    placement = 'auto',
    showWhenCollapsed = false,
    divider = 'none',
    className: consumerClassName,
    style: consumerStyle,
    'data-dt-name': dtName,
    'data-dt-properties': dtProperties,
  } = props;
  const { isMenuElement, isExpanded, didDrag, resetDidDrag } =
    useToolbarContext();
  const localRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useMergeRefs<HTMLButtonElement | null>([
    forwardedRef,
    localRef,
  ]);

  useEffect(() => {
    if (tabIndex === 0) {
      localRef.current?.focus();
    }
  }, [tabIndex]);

  const handleClick = (event: ReactMouseEvent) => {
    event.stopPropagation();

    if (didDrag) {
      resetDidDrag?.();
      return;
    }

    if (!disabled) {
      onClick(event);
    }
  };

  const handleSelect: ReactEventHandler = (event) => {
    event.stopPropagation();
    if (!disabled) {
      onClick(event);
    }
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (!disabled) {
      onKeyDown?.(event);
    }
  };

  const styledButton = (
    <Button
      ref={rootRef}
      className={clsx(buttonStyle({ disabled, selected }), consumerClassName)}
      style={consumerStyle}
      variant="default"
      tabIndex={tabIndex}
      data-testid={testId}
      aria-label={tooltipText}
      onKeyDown={handleKeyDown}
      onMouseUp={handleClick}
      size="condensed"
      data-dt-children-name={dtName}
      data-dt-properties={dtProperties}
    >
      <Button.Prefix>{prefixIcon}</Button.Prefix>
    </Button>
  );

  if (isMenuElement) {
    if (placement === 'toolbar' || (placement === 'auto' && isExpanded)) {
      return null;
    }

    const horizontalDivider = (
      <Divider
        className={styledHorizontalDivider}
        orientation="horizontal"
        as="hr"
      />
    );

    return (
      <>
        {divider === 'left' && horizontalDivider}
        <Menu.Item
          className={menuItemStyles({ disabled, selected })}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          data-dt-children-name={dtName}
          data-dt-properties={dtProperties}
        >
          <Menu.Prefix>{prefixIcon}</Menu.Prefix>
          <div className={styledMenuItemContent}>
            <span className={styledText}>{tooltipText}</span>
            {(menuShortcut || tooltipKeyShortcut) && (
              <span className={styledShortcutGroup({ disabled })}>
                {menuShortcut ?? (
                  <KeyboardShortcut
                    keys={`${tooltipKeyShortcut}`}
                    variant="minimal"
                    disabled={disabled}
                  />
                )}
              </span>
            )}
          </div>
        </Menu.Item>
        {divider === 'right' && horizontalDivider}
      </>
    );
  }

  if (placement === 'menu' || (!isExpanded && !showWhenCollapsed)) {
    return null;
  }

  const verticalDivider = (
    <Divider className={styledVerticalDivider} orientation="vertical" as="hr" />
  );
  let computedControlButton = styledButton;

  if (tooltipText) {
    if (tooltipKeyShortcut) {
      computedControlButton = (
        <KeyboardShortcutTooltip
          keys={tooltipKeyShortcut}
          text={tooltipText}
          disabled={hideTooltip}
          shortcutDisabled={disabled}
        >
          {styledButton}
        </KeyboardShortcutTooltip>
      );
    } else {
      computedControlButton = (
        <Tooltip text={tooltipText} disabled={hideTooltip}>
          {styledButton}
        </Tooltip>
      );
    }
  }

  return (
    <>
      {(divider === 'left' || (placement === 'toolbar' && !isExpanded)) &&
        verticalDivider}
      {computedControlButton}
      {divider === 'right' && verticalDivider}
    </>
  );
});

(Control as typeof Control & { displayName: string }).displayName =
  'ChartToolbar.Control';
